import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { Modal, Button } from "react-bootstrap";
import { Input, Select } from "../../components/Form";
import Breadcrumbs from "../../components/Breadcrumbs";

import { ToastContext } from "../../contexts/toast/toastContext";
import { ToastErrorContext } from "../../contexts/toast/toastErrorContext";
import { SweetAlertContext } from "../../contexts/sweetAlert/alertContext";

import { Form } from "@unform/web";
import { SubmitHandler, FormHandles } from "@unform/core";
import getValidationErrors from "../../helpers/getValidateErrors";
import { getSingleErrorMessage } from "../../helpers/getSingleErrorMessage";
import * as Yup from "yup";

import DataTable from "../../components/DataTable";
import DataNotFound from "../../components/DataNotFound";
import { debounce } from "lodash";
import api from "../../services/api";

import { IFormGrupo, IListGrupo } from "../../common/interfaces/grupos.interfaces";
import { toast } from "react-toastify";

const Grupos: React.FC = () => {

  //Constants
  const comum = [
    { label: "Sim", value: true },
    { label: "Não", value: false },
  ];

  const tipoMaterial = [
    { label: "Perfis", value: "PERFIL" },
    { label: "Componentes", value: "COMPONENTE" },
    { label: "Vidros", value: "VIDRO" },
    { label: "Superficies", value: "SUPERFICIE" },
    { label: "Kits", value: "KIT" },
  ];

  // Refs
  const formRef = useRef<FormHandles>(null);

  //Fields Typed
  const DataTableTS: any = DataTable;

  //Context
  const sweetAlertContext = useContext(SweetAlertContext);
  const toastContext = useContext(ToastContext);
  const toastErrorContext = useContext(ToastErrorContext);

  //States
  const [tableData, setTableData] = useState<IListGrupo[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [selected, setSelected] = useState<IFormGrupo>({} as IFormGrupo);
  const [novo, setNovo] = useState(false);
  const [reloadTable, setReloadTable] = useState(false);

  //Effects
  useEffect(() => {
    (async () => {
      try {
        setTableLoading(true);
        const { data } = await api.get("grupo/lista");
        setTableData(data);
      } catch (error) {
      } finally {
        setTableLoading(false);
      }
    })();
  }, [reloadTable]);

  //Handles
  const handleClose = () => setShow(false);

  const handleNew = () => {
    setNovo(true);
    setSelected({} as IFormGrupo);
    setShow(true);
  };

  const handleChangeFilter = useCallback(
    debounce(async (e) => {
      const { data } = await api.get(`grupo/lista/${e.target.value}`);
      setTableData(data);
    }, 500),
    []
  );

  const handleEdit = useCallback(async ({ id }) => {
    const { data } = await api.get(`grupo/${id}`);

    setSelected(data);
    setShow(true);
    setNovo(false);
  }, []);

  const handleDelete = ({ id, descricao }: IListGrupo) => {
    const title = `Excluir "${descricao}"?`;
    const message = "Esse grupo será excluída, isso não pode ser desfeito.";
    sweetAlertContext.danger(title, message, async () => {
      try {
        await api.delete(`grupo/${id}`);
        setTableData((prevState) =>
          prevState.filter(function (item) {
            return item.id !== id;
          })
        );
        sweetAlertContext.close();
      } catch (error) {
        const message = getSingleErrorMessage(error);
        if (message) {
          toastContext.notification(message, "warn");
        } else {
          toastContext.notification(
            "Ocorreu um erro ao realizar essa operação.",
            "error"
          );
        }
      }
    });
  };

  const handleSubmit: SubmitHandler<any> = async (grupo) => {
    try {
      formRef.current?.setErrors({});

      const message = "Campo obrigatório";
      const schema = Yup.object().shape({
        codigo: Yup.string().required(message)
        .max(20, 'O código só pode ter no máximo  20 caracteres.'),
        descricao: Yup.string().required(message)
        .max(100, 'A descrição só pode ter no máximo  100 caracteres.'),
        comum: Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable()
          .required(message),
        tipoMaterial: Yup.array().nullable().min(1, message).required(message),
      });
      await schema.validate(grupo, { abortEarly: false });

      //Passou pela validação
      grupo.id = selected.id;
      await api.post('grupo', grupo);

      setReloadTable(reload => !reload);
      setShow(false);

      toast.success("A ação foi concluída.");
    } catch (err) {
      toastErrorContext.notification(err);
      formRef.current?.setErrors(getValidationErrors(err));
    }
  };

  const handleSave = () => {
    formRef.current?.submitForm();
    setNovo(false);
  };

  const columns = useMemo(
    () => [
      {
        Header: "Codigo",
        accessor: "codigo",
        display: "align-middle",
      },
      {
        Header: "Descrição",
        accessor: "descricao",
        display: "align-middle",
      },
      {
        Header: "Material",
        accessor: "tipoMaterial",
        display: "align-middle",
      },
      {
        Header: "Comum",
        accessor: "comum",
        display: "align-middle",
      },
      {
        Header: "Ação",
        accessor: "acao",
        disableSortBy: true,
        display: "align-middle column-table-2b",
        Cell: ({ row: { original } }: { row: { original: IListGrupo } }) => (
          <>
            <Button
              title="Editar"
              variant="ghost-dark"
              className="p-2"
              onClick={() => handleEdit(original)}
            >
              <i className="far fa-edit icon m-0"></i>
            </Button>
            <Button
              title="Excluir"
              variant="ghost-dark"
              className="p-2"
              onClick={() => handleDelete(original)}
            >
              <i className="far fa-trash-alt icon m-0"></i>
            </Button>
          </>
        ),
      },
    ],
    []
  );

  return (
    <>
      <div className="content">
        <div className="container-xl">
          <div className="page-header">
            <div className="row">
              <Breadcrumbs />
              <span className="col-auto ml-auto">
                <Button variant="white" onClick={() => handleNew()}>
                  Novo Grupo
                </Button>
              </span>
              <div className="col-auto ml-0 pr-0">
                <div className="btn-list pr-0">
                  <span className="d-none d-sm-inline">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Busca..."
                      onChange={handleChangeFilter}
                    />
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="card p-0">
              <div className="card-body">
                <div className={'dimmer' + (tableLoading ? ' active' : '')}>
                  <div className="loader"></div>
                  <div className={"dimmer-content" + (tableLoading ? ' medium-box-loader' : '')} >
                    {tableData && (
                      <DataTableTS
                        columns={columns}
                        data={tableData}
                        bordered={false}
                        initialState={{
                          sortBy: [
                            {
                              id: "id",
                              desc: false,
                            },
                          ],
                        }}
                      />
                    )}
                    <DataNotFound
                      visible={
                        tableLoading == false ? tableData?.length === 0 : false
                      }
                    ></DataNotFound>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Grupo */}
      <Modal size="lg" show={show} onHide={handleClose} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{novo ? "Adicionar Grupo" : "Editar Grupo"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form initialData={selected} className="mt-3" ref={formRef} onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group col">
                <label className="form-label">Código</label>
                <Input
                  type="text"
                  name="codigo"
                  className="form-control"
                  placeholder="Código..."
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group col">
                <label className="form-label">Descrição</label>
                <Input
                  type="text"
                  name="descricao"
                  className="form-control"
                  placeholder="Descrição..."
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group col">
                <label className="form-label">Tipo de material</label>
                <Select name="tipoMaterial"
                  placeholder="Selecione uma opção..."
                  options={tipoMaterial}
                  isMulti={true}
                  defaultValue={selected.tipoMaterial} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group col">
                <label className="form-label">Grupo comum?</label>
                <Select
                  name="comum"
                  placeholder="Selecione uma opção..."
                  options={comum}
                  defaultValue={selected.comum}
                ></Select>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Fechar
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Grupos;
